import { Card, Divider, Grid, Hidden, Skeleton, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { useEffect, useMemo, useState } from 'react';
import { IUserModel } from '../../../../recoil/atoms/user';
import { methodNameEnum } from '../../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../../services/rest/makeAPIRequest';
import {
  getPercentComparedToAverage,
  roundTwo,
  secondsToHHMMSS,
} from '../utils/calculations';
import AgentMetricPanel from './AgentMetricPanel';

const useStyles = makeStyles((theme: Theme) => ({
  skeletonLoaderCard: {
    margin: '8px 48px 24px 48px',
    '& .MuiSkeleton-root': {
      backgroundColor: theme.palette.mode === 'dark' ? 'dimgray' : 'gainsboro',
    },
  },
  skeletonRow: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignContent: 'center',
    height: '100%',
    padding: '12px',
  },
}));

const AgentMetrics = (props) => {
  const { callMetricsLoadable, loading } = props;
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);

  const [
    _,
    businessUserCurrentLoadable,
    refreshToggleForBusinessUsers,
  ] = makeApiRequestCached<IUserModel | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );

  useEffect(() => {
    refreshToggleForBusinessUsers({});
  }, []);

  useEffect(() => {
    setIsLoading(businessUserCurrentLoadable?.state === loading || loading);
  }, [businessUserCurrentLoadable, loading]);

  const loader = (
    <div>
      {Array(5)
        .fill(0)
        .map((k, i) => (
          <Card
            key={i}
            variant="outlined"
            className={classes.skeletonLoaderCard}
          >
            <Grid
              container
              spacing={2}
              direction="row"
              justifyContent="space-around"
            >
              <Grid item xs={12} sm={3} md={3} lg={3} xl={3}>
                <div className={classes.skeletonRow}>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </div>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
                <div className={classes.skeletonRow}>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </div>
              </Grid>
              <Divider orientation="vertical" flexItem />
              <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
                <div className={classes.skeletonRow}>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </div>
              </Grid>
            </Grid>
          </Card>
        ))}
    </div>
  );

  const callMetrics = useMemo(() => {
    if (
      callMetricsLoadable?.state === 'hasValue' &&
      businessUserCurrentLoadable.state === 'hasValue'
    ) {
      const agents = callMetricsLoadable?.contents?.response?.data?.agents
        ?.length
        ? [...(callMetricsLoadable?.contents?.response?.data?.agents ?? [])]
        : [];

      const charts = callMetricsLoadable?.contents?.response?.data?.charts;

      const sorted = (sortBy) =>
        agents?.sort?.((a, b) => {
          return b?.[sortBy] - a?.[sortBy];
        });

      const getAverage = (values, getValue) =>
        values.length === 0
          ? 0
          : values.reduce((acc, a) => acc + (getValue(a) ?? 0), 0) /
            values.length;

      const getTopFive = (values, getValue, average) =>
        values.filter((a) => getValue(a) >= average).slice(0, 5);

      const getBottomFive = (values, getValue, average) => {
        const belowAvg = values.filter((a) => getValue(a) < average);
        const hasNonZeroValues = belowAvg.length && getValue(belowAvg[0]) > 0;
        if (hasNonZeroValues) {
          // remove zeros after first 5 values
          const firstZeroIndex = belowAvg.findIndex(
            (a, i) => i >= 5 && getValue(a) === 0
          );
          if (firstZeroIndex !== -1) {
            belowAvg.splice(firstZeroIndex);
          }
        }
        return belowAvg.slice(-5);
      };

      const sortedConnections = sorted('connections');
      const getConnections = (a) => a?.connections;
      const averageConnections =
        charts?.connections?.average ??
        getAverage(sortedConnections, getConnections);
      const topConnections = getTopFive(
        sortedConnections,
        getConnections,
        averageConnections
      );
      const bottomConnections = getBottomFive(
        sortedConnections,
        getConnections,
        averageConnections
      );

      const sortedMissed = sorted('missedCalls');
      const getMissedCalls = (a) => a?.missedCalls;
      const averageMissed =
        charts?.calls?.missed?.average ??
        getAverage(sortedMissed, getMissedCalls);
      const topMissed = getTopFive(sortedMissed, getMissedCalls, averageMissed);
      const bottomMissed = getBottomFive(
        sortedMissed,
        getMissedCalls,
        averageMissed
      );

      const sortedTalkTime = sorted('talkTime');
      const getTalkTime = (a) => a?.talkTime;
      const averageTalkTime =
        charts?.callTime?.talk?.average ??
        getAverage(sortedTalkTime, getTalkTime);
      const topTalkTime = getTopFive(
        sortedTalkTime,
        getTalkTime,
        averageTalkTime
      );
      const bottomTalkTime = getBottomFive(
        sortedTalkTime,
        getTalkTime,
        averageTalkTime
      );

      const sortedTotalCalls = sorted('totalCalls');
      const getTotalCalls = (a) => a?.totalCalls;
      const averageTotalCalls =
        charts?.calls?.average ?? getAverage(sortedTotalCalls, getTotalCalls);
      const topTotalCalls = getTopFive(
        sortedTotalCalls,
        getTotalCalls,
        averageTotalCalls
      );
      const bottomTotalCalls = getBottomFive(
        sortedTotalCalls,
        getTotalCalls,
        averageTotalCalls
      );

      const getAgentName = (userid) => {
        const currentAgent = businessUserCurrentLoadable?.contents?.response?.data?.result?.find(
          (u) => +u.userid === +userid
        );
        const firstName = currentAgent?.fname ?? '';
        const lastInitial = currentAgent?.lname.charAt(0) ?? '';
        const displayName = firstName + ' ' + lastInitial;
        return displayName;
      };

      return [
        {
          title: 'Total Calls',
          average: roundTwo(charts?.calls?.average),
          topAgents: topTotalCalls?.map((agent) => ({
            name: getAgentName(agent?.userid),
            agentid: agent?.userid,
            total: roundTwo(agent?.totalCalls),
            percent: getPercentComparedToAverage(
              agent?.totalCalls,
              charts?.calls?.average
            ),
          })),
          bottomAgents: bottomTotalCalls.map((agent) => ({
            name: getAgentName(agent?.userid),
            total: roundTwo(agent?.totalCalls),
            agentid: agent?.userid,
            percent: getPercentComparedToAverage(
              agent?.totalCalls,
              charts?.calls?.average
            ),
          })),
        },
        {
          title: 'Connections',
          average: roundTwo(charts?.connections?.average),
          topAgents: topConnections?.map((agent, i) => ({
            name: getAgentName(agent?.userid),
            total: roundTwo(agent?.connections),
            agentid: agent?.userid,
            percent: getPercentComparedToAverage(
              agent?.connections,
              charts?.connections?.average
            ),
          })),
          bottomAgents: bottomConnections?.map((agent, i) => ({
            name: getAgentName(agent?.userid),
            total: roundTwo(agent?.connections),
            agentid: agent?.userid,
            percent: getPercentComparedToAverage(
              agent?.connections,
              charts?.connections?.average
            ),
          })),
        },
        {
          title: 'Talk Time',
          average: secondsToHHMMSS(charts?.callTime?.talk?.average),
          topAgents: topTalkTime?.map((agent) => ({
            name: getAgentName(agent?.userid),
            total: secondsToHHMMSS(agent?.talkTime),
            agentid: agent?.userid,
            percent: getPercentComparedToAverage(
              agent?.talkTime,
              charts?.callTime?.talk?.average
            ),
          })),
          bottomAgents: bottomTalkTime?.map((agent) => ({
            name: getAgentName(agent?.userid),
            total: roundTwo(agent?.talkTime),
            agentid: agent?.userid,
            percent: getPercentComparedToAverage(
              agent?.talkTime,
              charts?.callTime?.talk?.average
            ),
          })),
        },
        {
          title: 'Missed Calls',
          average: roundTwo(charts?.calls?.missed?.average),
          topAgents: topMissed?.map((agent) => ({
            name: getAgentName(agent?.userid),
            total: roundTwo(agent?.missedCalls),
            agentid: agent?.userid,
            percent: getPercentComparedToAverage(
              agent?.missedCalls,
              charts?.calls?.missed?.average
            ),
          })),
          bottomAgents: bottomMissed?.map((agent) => ({
            name: getAgentName(agent?.userid),
            total: roundTwo(agent?.missedCalls),
            agentid: agent?.userid,
            percent: getPercentComparedToAverage(
              agent?.missedCalls,
              charts?.calls?.missed?.average
            ),
          })),
        },
      ];
    }
  }, [callMetricsLoadable, businessUserCurrentLoadable]);

  return (
    <div>
      <Grid container spacing={1} direction="row" justifyContent="space-around">
        <Grid item md={2} lg={3} xl={3}></Grid>
        <Grid item md={4} lg={4} xl={4}>
          <Hidden smDown>
            <div style={{ fontSize: '16px' }}>Above Average</div>
          </Hidden>
        </Grid>
        <Grid item md={4} lg={4} xl={4}>
          <Hidden smDown>
            <div style={{ fontSize: '16px' }}>Below Average</div>
          </Hidden>
        </Grid>
      </Grid>
      {isLoading ? (
        loader
      ) : (
        <div>
          {callMetrics?.map((t, k) => (
            <div key={k} style={{ margin: '8px 48px 16px' }}>
              <AgentMetricPanel
                metric={t}
                topAgents={t?.topAgents}
                loading={isLoading}
                coachableAgents={t?.bottomAgents}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentMetrics;
